import { useToast } from '@chakra-ui/react';
import React, { createContext, useState, useEffect, ReactNode } from 'react';
import cms from '../apis/cms';

interface Location {
  name: string;
  slug: string;
}

interface MenuLocationsContextType {
  data: Location[];
  createLocation: (newLocation: { name: string }) => Promise<void>;
  deleteLocation: (locationId: string) => Promise<void>;
  updateLocation: (
    locationId: string,
    location: { name: string },
  ) => Promise<void>;
  loading?: boolean;
}

const MenuLocationsContext = createContext<MenuLocationsContextType>({
  data: [],
  createLocation: () => Promise.resolve(),
  deleteLocation: () => Promise.resolve(),
  updateLocation: () => Promise.resolve(),
  loading: false,
});

interface MenuLocationsProviderProps {
  children: ReactNode;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const MenuLocationsProvider = ({ children }: MenuLocationsProviderProps) => {
  const [data, setData] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const toast = useToast();

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: dataReturn } = await cms.rest.get<Location[]>(
        '/menu-items-location/',
      );
      setData(dataReturn);
    } catch (error) {
      toast({
        title: 'Opss!!',
        description:
          'Erro ao carregar localizações de itens, tente novamente mais tarde',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createLocation = async (newLocation: { name: string }) => {
    setLoading(true);
    try {
      const { data: dataReturn } = await cms.rest.post<Location>(
        '/menu-items-location',
        { ...newLocation },
      );
      setData([...data, dataReturn]);
    } catch (error) {
      //
    } finally {
      setLoading(false);
    }
  };

  const updateLocation = async (
    locationId: string,
    dataParam: { name: string },
  ) => {
    setLoading(true);
    try {
      const { data: dataReturn } = await cms.rest.patch<any>(
        `/menu-items-location/${locationId}`,
        { ...dataParam },
      );
      const updatedData = data.map((item: any) => {
        if (item?.id === dataReturn?.id) {
          return dataReturn;
        }
        return item;
      });
      setData(updatedData);
    } catch (error) {
      //
    } finally {
      setLoading(false);
    }
  };

  const deleteLocation = async (locationId: string) => {
    setLoading(true);
    try {
      await cms.rest.delete(`menu-items-location/${locationId}`);
      setData(data.filter((location: any) => location?.id !== locationId));
    } catch (error) {
      //
    } finally {
      setLoading(false);
    }
  };

  return (
    <MenuLocationsContext.Provider
      value={{ data, createLocation, deleteLocation, updateLocation, loading }}
    >
      {children}
    </MenuLocationsContext.Provider>
  );
};

export { MenuLocationsContext, MenuLocationsProvider };
